
import React from 'react';
import { Link } from 'react-router-dom';
import { Tables } from '@/integrations/supabase/types';
import { format } from 'date-fns';
import { Calendar, Clock, ArrowRight, User, Eye } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

type BlogPost = Tables<'blog_posts'>;

interface BlogPostCardProps {
  post: BlogPost;
}

const BlogPostCard: React.FC<BlogPostCardProps> = ({ post }) => {
  const readingTime = Math.ceil((post.content?.length || 0) / 1000);

  return (
    <Card className="group overflow-hidden hover:shadow-2xl transition-all duration-500 border-0 shadow-lg hover:-translate-y-2 bg-white h-full flex flex-col">
      <Link to={`/blog/${post.slug}`} className="block h-full flex flex-col">
        {/* Image Section */}
        <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-blue-50 to-purple-50">
          {post.featured_image_url ? (
            <img
              src={post.featured_image_url}
              alt={post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 via-purple-50 to-indigo-100">
              <div className="text-6xl md:text-7xl font-bold text-gray-300 select-none">
                {post.title.charAt(0).toUpperCase()}
              </div>
            </div>
          )}
          
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Reading time badge */}
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium text-gray-700 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {readingTime} min
          </div>
        </div>
        
        {/* Content Section */}
        <CardContent className="p-6 flex-1 flex flex-col">
          <div className="space-y-4 flex-1">
            {/* Meta Information */}
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-3">
                {post.author_name && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <User className="w-3 h-3 text-white" />
                    </div>
                    <span className="font-medium">{post.author_name}</span>
                  </div>
                )}
              </div>
              
              {post.published_at && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <time dateTime={post.published_at}>
                    {format(new Date(post.published_at), 'MMM dd, yyyy')}
                  </time>
                </div>
              )}
            </div>

            {/* Title */}
            <h3 className="font-bold text-xl text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors leading-tight">
              {post.title}
            </h3>

            {/* Excerpt */}
            {(post.meta_description || post.content) && (
              <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed flex-1">
                {post.meta_description || post.content?.substring(0, 150) + '...'}
              </p>
            )}
          </div>

          {/* Read More Button */}
          <div className="flex items-center justify-between pt-4 mt-auto border-t border-gray-100">
            <div className="flex items-center text-blue-600 text-sm font-semibold group-hover:text-blue-700 transition-colors">
              <span>Read Article</span>
              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
            </div>
            
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <Eye className="w-3 h-3" />
              <span>View</span>
            </div>
          </div>
        </CardContent>
      </Link>
    </Card>
  );
};

export default BlogPostCard;
